import { InputWrapperProps } from '@mantine/core';
import { Combobox, useCombobox } from 'src/design/atoms/Combobox';
import { CloseButton } from 'src/design/atoms/CloseButton';
import { Input } from 'src/design/atoms/Input';
import { InputBase } from 'src/design/atoms/InputBase';
import React from 'react';

export interface ICreatableSelectItem {
  id: string;
  name?: string;
}

export interface ICreatableSelectProps<T extends ICreatableSelectItem>
  extends Omit<InputWrapperProps, 'onChange'> {
  items: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  onCreate: (name: string) => Promise<T | null>;
  emptyMessage: string;
  disabled?: boolean;
  clearable?: boolean;
  clearAriaLabel?: string;
  /** Clears the current value when the field blurs while searching with empty text. */
  clearValueOnEmptyBlur?: boolean;
}

/** @registry Combobox that filters `{ id, name }` items and offers an inline "Create …" option for unmatched search text; wrap it with a domain component that supplies data loading and `onCreate`. `clearable` adds a CloseButton; `clearValueOnEmptyBlur` clears the value when blurring with empty search. */
export function CreatableSelect<T extends ICreatableSelectItem>({
  items,
  value,
  onChange,
  onCreate,
  emptyMessage,
  label,
  placeholder,
  disabled = false,
  clearable = false,
  clearAriaLabel = 'Clear',
  clearValueOnEmptyBlur = false,
  ...props
}: ICreatableSelectProps<T>) {
  const [search, setSearch] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setIsSearching(false);
    },
  });

  const displayValue = isSearching ? search : value?.name || '';

  const q = isSearching ? search.toLowerCase().trim() : '';
  const filtered = items.filter((item) =>
    (item.name ?? '').toLowerCase().includes(q),
  );

  const options = filtered.map((item) => (
    <Combobox.Option value={item.id} key={item.id}>
      {item.name}
    </Combobox.Option>
  ));

  const showCreate =
    isSearching &&
    search.trim().length > 0 &&
    !filtered.some(
      (item) => (item.name ?? '').toLowerCase() === search.toLowerCase().trim(),
    );

  const showClear = clearable && !!value && !disabled;

  return (
    <Input.Wrapper label={label} {...props}>
      <Combobox
        store={combobox}
        onOptionSubmit={async (val) => {
          if (val === '$create') {
            onChange(await onCreate(search.trim()));
          } else {
            onChange(items.find((item) => item.id === val) ?? null);
          }
          setSearch('');
          setIsSearching(false);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            rightSection={
              showClear ? (
                <CloseButton
                  size="sm"
                  aria-label={clearAriaLabel}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(null);
                    setSearch('');
                    setIsSearching(false);
                  }}
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={showClear ? 'all' : 'none'}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              if (
                clearValueOnEmptyBlur &&
                isSearching &&
                search.trim() === ''
              ) {
                onChange(null);
              }
              setSearch('');
              setIsSearching(false);
            }}
            placeholder={placeholder}
            value={displayValue}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setIsSearching(true);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            disabled={disabled}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {options.length === 0 && !showCreate && (
              <Combobox.Empty>{emptyMessage}</Combobox.Empty>
            )}
            {options}
            {showCreate && (
              <Combobox.Option value="$create">
                Create &quot;{search.trim()}&quot;
              </Combobox.Option>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Input.Wrapper>
  );
}
